import {
  GOScheduleMenu,
  MenuTemplate,
  Coordinate,
  ClusterTarget,
  DateMenuSuggestion,
  DateMenuSuggestionImport,
} from '@types'
import {
  formatCutoffTime,
  formatNumberToTime,
} from './MenusFinder/GroupOrderMenusFinder'
export interface Client {
  id: string
  name: string
  latitude: number
  longitude: number
}

export const getMenuCoordinates = (
  menu: GOScheduleMenu,
  hq: number,
): Coordinate => {
  const longAndLat = menu.geoCoordinates[hq]

  return {
    latitude: longAndLat[1],
    longitude: longAndLat[0],
  }
}

export interface MenuTemplateOptions {
  template: MenuTemplate
  menuOptions: GOScheduleMenu[]
}

export const calcMenuTemplateOptions = (
  template: MenuTemplate,
  menus: GOScheduleMenu[],
): MenuTemplateOptions => {
  const menuOptions = menus.filter((menu) => {
    const matchChefIds =
      !template.chefIds ||
      template.chefIds.length === 0 ||
      template.chefIds.includes(menu.chef.id)
    const matchExclChefIds =
      !template.excludeChefIds ||
      template.excludeChefIds.length === 0 ||
      !template.excludeChefIds.includes(menu.chef.id)
    const matchCuisines =
      !template.cuisines ||
      template.cuisines.length === 0 ||
      menu.cuisineTags.some(
        (cuisineTag) => template.cuisines?.includes(cuisineTag),
      )
    const matchExclCuisines =
      !template.excludeCuisines ||
      template.excludeCuisines.length === 0 ||
      !menu.cuisineTags.some(
        (cuisineTag) => template.excludeCuisines?.includes(cuisineTag),
      )

    return (
      matchChefIds && matchExclChefIds && matchCuisines && matchExclCuisines
    )
  })

  return {
    template,
    menuOptions,
  }
}

export const getCoordinateKey = (coord: Coordinate): string => {
  return `${coord.latitude},${coord.longitude}`
}

export const getCoordinatesKey = (coords: Coordinate[]): string => {
  return coords.map(getCoordinateKey).join(',')
}

// Create a memoization cache for distance calculations
const distanceCache = new Map<string, number>()

export const calculateDistanceInMiles = (
  coord1: Coordinate,
  coord2: Coordinate,
): number => {
  // Create a cache key based on the coordinates
  const key = getCoordinatesKey([coord1, coord2])

  // If we've already calculated this distance, return it from cache
  if (distanceCache.has(key) && distanceCache.get(key) !== undefined) {
    return distanceCache.get(key) || 0.0
  }

  // Earth's radius in miles
  const earthRadius = 3958.8

  // Convert latitude and longitude from degrees to radians
  const latRad1 = (coord1.latitude * Math.PI) / 180
  const lonRad1 = (coord1.longitude * Math.PI) / 180
  const latRad2 = (coord2.latitude * Math.PI) / 180
  const lonRad2 = (coord2.longitude * Math.PI) / 180

  // Differences in coordinates
  const dLat = latRad2 - latRad1
  const dLon = lonRad2 - lonRad1

  // Haversine formula
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(latRad1) * Math.cos(latRad2) * Math.sin(dLon / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  // Distance in miles
  const distance = earthRadius * c

  // Store in cache for future use
  distanceCache.set(key, distance)

  return distance
}

/**
 * Finds the optimal path between coordinates that minimizes total distance
 * Implements an exhaustive approach for the Traveling Salesman Problem with a fixed endpoint
 * Since the problem size is limited (max 8 stops), this approach is feasible
 *
 * @param coordinates Array of coordinates (latitude/longitude pairs)
 * @param finalStop The coordinate that must be the final stop on the route
 * @returns Object containing the optimal path indices and total distance
 */
export const findOptimalPath = (
  coordinates: Coordinate[],
  finalStop?: Coordinate,
): { path: number[]; totalDistance: number } => {
  if (coordinates.length <= 1) {
    return {
      path: coordinates.length === 1 ? [0] : [],
      totalDistance: 0,
    }
  }

  // Find finalStop index
  const finalStopIndex = finalStop
    ? coordinates.findIndex(
        (coord) =>
          coord.latitude === finalStop.latitude &&
          coord.longitude === finalStop.longitude,
      )
    : -1

  // If finalStop is not in the array, return an empty path
  if (finalStopIndex === -1) {
    return {
      path: [],
      totalDistance: 0,
    }
  }

  const n = coordinates.length

  // Calculate distance matrix to avoid recalculating distances
  const distanceMatrix: number[][] = []
  for (let i = 0; i < n; i++) {
    distanceMatrix[i] = []
    for (let j = 0; j < n; j++) {
      if (i === j) {
        distanceMatrix[i][j] = 0
      } else {
        distanceMatrix[i][j] = calculateDistanceInMiles(
          coordinates[i],
          coordinates[j],
        )
      }
    }
  }

  // If there's only one stop besides the final stop
  if (n === 2) {
    const otherIndex = finalStopIndex === 0 ? 1 : 0

    return {
      path: [otherIndex, finalStopIndex],
      totalDistance: distanceMatrix[otherIndex][finalStopIndex],
    }
  }

  // Remove the finalStop from consideration for all but the last position
  const remainingIndices = Array.from({ length: n }, (_, i) => i).filter(
    (i) => i !== finalStopIndex,
  )
  let bestPath: number[] = []
  let bestDistance = Infinity

  // Helper function to build all possible permutations with pruning
  const findOptimalPermutation = (
    currentPath: number[],
    remaining: number[],
    currentDistance: number,
  ) => {
    // Base case: all locations visited
    if (remaining.length === 0) {
      // Add the finalStop
      const totalDistance =
        currentDistance +
        distanceMatrix[currentPath[currentPath.length - 1]][finalStopIndex]
      const completePath = [...currentPath, finalStopIndex]

      if (totalDistance < bestDistance) {
        bestDistance = totalDistance
        bestPath = completePath
      }

      return
    }

    // Pruning: if current distance already exceeds best, stop this branch
    if (currentDistance >= bestDistance) {
      return
    }

    // Try each remaining location as the next stop
    for (let i = 0; i < remaining.length; i++) {
      const nextLocation = remaining[i]
      const newRemaining = [...remaining.slice(0, i), ...remaining.slice(i + 1)]

      const additionalDistance =
        currentPath.length === 0
          ? 0 // First stop has no incoming distance
          : distanceMatrix[currentPath[currentPath.length - 1]][nextLocation]

      // Only explore this path if it's potentially better than our current best
      const newDistance = currentDistance + additionalDistance
      if (newDistance < bestDistance) {
        findOptimalPermutation(
          [...currentPath, nextLocation],
          newRemaining,
          newDistance,
        )
      }
    }
  }

  // Start the recursive permutation building
  findOptimalPermutation([], remainingIndices, 0)

  return {
    path: bestPath,
    totalDistance: bestDistance,
  }
}

/**
 * Calculates the total distance of a path
 *
 * @param coordinates Array of coordinates
 * @param path Array of indices representing the path
 * @returns Total distance in miles
 */
export const calculatePathDistance = (
  coordinates: Coordinate[],
  path: number[],
): number => {
  if (path.length <= 1) {
    return 0
  }

  let totalDistance = 0
  for (let i = 0; i < path.length - 1; i++) {
    totalDistance += calculateDistanceInMiles(
      coordinates[path[i]],
      coordinates[path[i + 1]],
    )
  }

  return totalDistance
}

export interface MenuTemplateSuggestionsResult {
  suggestions: DateMenuSuggestion[]
  deadendTemplateIndex?: number
}

export interface MenuTemplateSuggestionsOptions {
  templateIdx?: number
  maxDistance?: number
}

/**
 * Generates menu template suggestions using a greedy approach to minimize memory usage
 * Each menu and chef can only be used once within a suggestion
 * Each suggestion will include exactly one menu for each template option
 * Each suggestion will only include menus with unique cuisinesId values which means each menu on a suggestion must have a unique combination of cuisine tags
 *
 * @param menuTemplateOptions Array of template options with their matching menus
 * @param targets Array of targets (client locations or chefs)
 * @param hqId The headquarters ID for coordinate lookup
 * @returns Array of MenuTemplateSuggestion objects sorted by minimum total distance
 */
export const generateMenuTemplateSuggestions = (
  menuTemplateOptions: MenuTemplateOptions[],
  targets: ClusterTarget[],
  hqId: number,
  options: MenuTemplateSuggestionsOptions[] = [],
): MenuTemplateSuggestionsResult => {
  // No template options means no suggestions
  if (menuTemplateOptions.length === 0) {
    return { suggestions: [] }
  }

  // Clear the distance cache at the start of each call to avoid memory leaks
  distanceCache.clear()

  // Helper function to get or calculate the distance from client to a menu
  const getMenuDistance = (
    menu: GOScheduleMenu,
    target: Coordinate,
  ): number => {
    const menuCoord = getMenuCoordinates(menu, hqId)
    const distance = calculateDistanceInMiles(target, menuCoord)

    return distance
  }

  // Number of suggestions to generate (limit to a reasonable number to prevent memory issues)
  const maxSuggestionCount = 5000
  const suggestions: DateMenuSuggestion[] = []

  // Helper function to check if a menu can be used in a selection
  const canUseMenu = (
    menu: GOScheduleMenu,
    menuTemplate: MenuTemplate,
    targetCoordinates: Coordinate,
    selectedMenus: GOScheduleMenu[],
    usedChefIds: Set<string>,
    usedCuisineIds: Set<string>,
    menuUsage: { [key: string]: number },
    options?: MenuTemplateSuggestionsOptions,
  ): boolean => {
    // Check if this menu has already been selected
    if (selectedMenus.some((selectedMenu) => selectedMenu.id === menu.id)) {
      return false
    }

    // Check if this chef has already been used
    if (usedChefIds.has(menu.chef.id)) {
      return false
    }

    // Check if this cuisinesId has already been used
    if (usedCuisineIds.has(menu.cuisinesId)) {
      return false
    }

    // Check if this menu has already been used too many times
    if (menuUsage[menu.id] && menuUsage[menu.id] >= menuTemplate.maxNumReuses) {
      return false
    }

    if (
      options?.maxDistance &&
      getMenuDistance(menu, targetCoordinates) > options.maxDistance
    ) {
      return false
    }

    return true
  }

  const deadendCombinations: { [key: string]: number } = {}
  let reachedDeadend = false
  let deadendTemplateIndex = 0
  let suggestionId = 0

  // Greedy function to build one suggestion
  const buildSuggestion = (
    target: ClusterTarget,
    menuUsage: { [key: string]: number } = {},
  ): DateMenuSuggestion | null => {
    const selectedMenus: GOScheduleMenu[] = []
    const usedChefIds = new Set<string>()
    const usedCuisineIds = new Set<string>()

    // Process each template option in order
    for (let i = 0; i < menuTemplateOptions.length; i++) {
      const templateOption = menuTemplateOptions[i]
      const deadendKey = selectedMenus.map((menu) => menu.id).join(',')
      const canUseMenuOptions = options.find(
        (option) =>
          option.templateIdx === i || option.templateIdx === undefined,
      )

      // Filter out menus that have constraints (already used chef, cuisine, or menu)
      const availableMenus = templateOption.menuOptions.filter((menu) =>
        canUseMenu(
          menu,
          templateOption.template,
          target.coordinates,
          selectedMenus,
          usedChefIds,
          usedCuisineIds,
          menuUsage,
          canUseMenuOptions,
        ),
      )

      if (availableMenus.length === 0) {
        // No valid menu for this template, can't complete the suggestion
        if (!deadendCombinations[deadendKey]) {
          deadendCombinations[deadendKey] = 0
          if (selectedMenus.length - 1 > deadendTemplateIndex) {
            deadendTemplateIndex = selectedMenus.length
          }
        }
        deadendCombinations[deadendKey]++

        return {
          id: 0,
          target,
          menus: selectedMenus,
          totalDistance: 0,
          route: [],
        }
      }

      // Sort menus by distance from client, with a variable randomness factor
      const sortedMenus = [...availableMenus].sort((a, b) => {
        const distA = getMenuDistance(a, target.coordinates)
        const distB = getMenuDistance(b, target.coordinates)

        return distA - distB
      })

      // Pick the best menu
      let selectedIndex = 0
      let selectedMenu = sortedMenus[selectedIndex]
      let nextDeadendKey = [
        ...selectedMenus.map((menu) => menu.id),
        selectedMenu.id,
      ].join(',')

      // Skip deadend combinations
      while (deadendCombinations[nextDeadendKey]) {
        selectedIndex += deadendCombinations[nextDeadendKey]
        if (selectedIndex >= sortedMenus.length && selectedMenus.length === 0) {
          return null
        }

        // Reached a deadend on all menus for a menu template, backtrack one template
        if (selectedIndex >= sortedMenus.length) {
          const backtrackDeadendKey = selectedMenus
            .map((menu) => menu.id)
            .join(',')
          if (!deadendCombinations[backtrackDeadendKey]) {
            deadendCombinations[backtrackDeadendKey] = 0
          }
          deadendCombinations[backtrackDeadendKey]++

          return {
            id: 0,
            target,
            menus: selectedMenus,
            totalDistance: 0,
            route: [],
          }
        }
        selectedMenu = sortedMenus[selectedIndex]
        nextDeadendKey = [
          ...selectedMenus.map((menu) => menu.id),
          selectedMenu.id,
        ].join(',')
      }

      // Add the selected menu
      selectedMenus.push(selectedMenu)
      usedChefIds.add(selectedMenu.chef.id)
      usedCuisineIds.add(selectedMenu.cuisinesId)
    }

    // We have a complete suggestion, calculate its path and distance
    const coordinates: Coordinate[] = []
    selectedMenus.forEach((menu) => {
      coordinates.push(getMenuCoordinates(menu, hqId))
    })
    coordinates.push(target.coordinates)

    // Find optimal path with client coordinate as the final stop
    const { path, totalDistance } = findOptimalPath(
      coordinates,
      target.coordinates,
    )

    return {
      id: suggestionId++,
      target,
      menus: selectedMenus,
      totalDistance,
      route: path,
    }
  }

  let targetIdx = 0

  // Generate the first suggestion with distance priority
  const firstSuggestion = buildSuggestion(targets[targetIdx])
  if (
    firstSuggestion &&
    firstSuggestion.menus.length === menuTemplateOptions.length
  ) {
    suggestions.push(firstSuggestion)

    // Mark all menus in this suggestion as used
    const menuUsage: { [key: string]: number } = {}
    firstSuggestion.menus.forEach((menu) => {
      if (!menuUsage[menu.id]) {
        menuUsage[menu.id] = 0
      }
      menuUsage[menu.id]++
    })

    // Generate additional suggestions with increasing randomness
    for (let i = 1; i < maxSuggestionCount; i++) {
      targetIdx = (targetIdx + 1) % targets.length
      const target = targets[targetIdx]
      const newSuggestion = buildSuggestion(target, menuUsage)

      // If menus length is not equal to number of template options it means we hit a deadend
      if (
        newSuggestion &&
        newSuggestion.menus.length === menuTemplateOptions.length
      ) {
        suggestions.push(newSuggestion)

        // Update used menu IDs
        newSuggestion.menus.forEach((menu) => {
          if (!menuUsage[menu.id]) {
            menuUsage[menu.id] = 0
          }
          menuUsage[menu.id]++
        })
      } else if (!newSuggestion) {
        // Reached a deadend on all menu templates
        reachedDeadend = true
        break
      }
    }
  }

  // Sort by total distance (ascending)
  return {
    suggestions: suggestions.sort((a, b) => a.totalDistance - b.totalDistance),
    deadendTemplateIndex: reachedDeadend ? deadendTemplateIndex : undefined,
  }
}

/**
 * Selects the optimal set of non-overlapping menu template suggestions
 * No menu will appear in more than one of the returned suggestions
 * Prioritizes suggestions with minimum total distance
 *
 * @param suggestions Array of menu template suggestions
 * @param maxResults Maximum number of suggestions to return
 * @returns Array of non-overlapping suggestions with minimum total distance
 */
export const getOptimalNonOverlappingSuggestions = (
  suggestions: DateMenuSuggestion[],
  maxResults: number,
): DateMenuSuggestion[] => {
  // Early return for edge cases
  if (suggestions.length === 0 || maxResults <= 0) {
    return []
  }

  // Make a shallow copy and ensure suggestions are sorted by total distance
  const sortedSuggestions = [...suggestions].sort(
    (a, b) => a.totalDistance - b.totalDistance,
  )
  const result: DateMenuSuggestion[] = []
  const usedMenuIds = new Set<string>()

  // Helper function to check if a suggestion has any overlapping menus with already selected ones
  const hasOverlappingMenus = (suggestion: DateMenuSuggestion): boolean => {
    return suggestion.menus.some((menu) => usedMenuIds.has(menu.id))
  }

  // Process each suggestion in order of increasing distance
  for (const suggestion of sortedSuggestions) {
    // Skip if we've already selected the max number of suggestions
    if (result.length >= maxResults) {
      break
    }

    // Skip this suggestion if it has any menus that overlap with already selected suggestions
    if (hasOverlappingMenus(suggestion)) {
      continue
    }

    // Add this suggestion to the result
    result.push(suggestion)

    // Mark all menus in this suggestion as used
    suggestion.menus.forEach((menu) => {
      usedMenuIds.add(menu.id)
    })
  }

  return result
}

const menuToCSVRow = (menu: GOScheduleMenu, includeEditableTag = true): any => {
  return {
    [`${includeEditableTag ? 'Menu ID (editable)' : 'Menu ID'}`]: menu.id,
    'Menu Card Name': menu.name.replace(/"/g, "'"),
    Vendor: menu.chef.name.replace(/"/g, "'"),
    'Menu Card Cutoff Time': formatCutoffTime(
      menu.groupOrderCutoffDays,
      menu.groupOrderCutoffTime,
    ),
    'Menu Items': menu.allMenuItems
      .map((item) => item.name)
      .join(', ')
      .replace(/"/g, "'"),
    'Cuisine(s)': menu.cuisineTags.join(', ').replace(/"/g, "'"),
    'Ready to Heat': menu.readyToHeat ? 'Yes' : 'No',
    'Avg Menu Item Price': menu.avgPrice,
    'Avg Menu Margin': menu.margin.toFixed(2) + '%',
    'Vendor Pickup Time (On Date)': menu.pickupTime,
    'Vendor Earliest Pickup Time': formatNumberToTime(
      menu.groupOrderEarliestPickupTime,
    ),
    'Vendor Max Items / Day': menu.vendorMaxGOItems,
    'Vendor Menucard Cap': menu.menuMaxItems,
    'Client Name': menu.clientName.replace(/"/g, "'"),
    'Last Ordered by Client': menu.lastOrdered,
    'Distance (miles) from Client':
      menu.distance !== '' ? menu.distance : 'N/A',
    'Avg Client Menu Item Rating': menu.avgMenuItemRatingByClient || 'N/A',
  }
}

export const menusToCSV = (menus: GOScheduleMenu[]): any[] => {
  if (!menus || menus.length === 0) {
    return []
  }

  return menus.map((menu) => menuToCSVRow(menu, false))
}

export const dateMenuSuggestionToCSV = (
  dateMenuSuggestions: DateMenuSuggestion[] | null,
): any[] => {
  if (!dateMenuSuggestions || dateMenuSuggestions.length === 0) {
    return []
  }

  const csvData: any[] = []

  dateMenuSuggestions.forEach((suggestion, suggestionIndex) => {
    // For each menu in the suggestion
    suggestion.menus.forEach((menu, menuIndex) => {
      // Get stop number from route index
      const stopNumber = suggestion.route.indexOf(menuIndex) + 1

      // Create row for this menu
      const row = {
        'Date Menu Number (editable)': suggestionIndex + 1,
        'Target ID (editable)': suggestion.target.id,
        Target: suggestion.target.name,
        'Total Distance': suggestion.totalDistance,
        'Stop Number (editable)': stopNumber,
        ...menuToCSVRow(menu),
      }

      csvData.push(row)
    })
  })

  return csvData
}

export const csvToDateMenuSuggestions =
  (
    displayFailureMessage: (message: string) => void,
    targets: ClusterTarget[],
    menus: GOScheduleMenu[],
    hqId: number,
  ) =>
  (data: any[], _fileInfo: any): DateMenuSuggestion[] | undefined => {
    try {
      if (targets.length === 0) {
        displayFailureMessage('No targets included')

        return
      }

      if (!data || data.length === 0) {
        displayFailureMessage('No valid data found in CSV file')

        return
      }

      // Group rows by Date Menu Number to collect menus for each suggestion
      const suggestionMap: Record<string, DateMenuSuggestionImport> = {}

      // Process each row in the CSV
      data.forEach((row, rowIdx) => {
        if (
          !row ||
          !row['Menu ID (editable)'] ||
          !row['Date Menu Number (editable)']
        ) {
          return
        }

        const dateMenuNumber = String(row['Date Menu Number (editable)'])
        const stopNumber = parseInt(String(row['Stop Number (editable)']), 10)
        const menuId = String(row['Menu ID (editable)'])
        const targetId = String(row['Target ID (editable)'])
        if (isNaN(stopNumber) || stopNumber <= 0) {
          displayFailureMessage(`Invalid stop number for row ${rowIdx + 1}`)

          return
        }

        if (!dateMenuNumber) {
          displayFailureMessage(
            `Invalid Date Menu number for row ${rowIdx + 1}`,
          )

          return
        }

        if (!menuId) {
          displayFailureMessage(`Invalid Menu ID for row ${rowIdx + 1}`)

          return
        }

        const target = targets.find((target) => target.id === targetId)
        if (!target) {
          displayFailureMessage(`Invalid Target ID for row ${rowIdx + 1}`)

          return
        }

        // Initialize suggestion if it doesn't exist
        if (!suggestionMap[dateMenuNumber]) {
          suggestionMap[dateMenuNumber] = {
            id: parseInt(dateMenuNumber, 10),
            menuIds: [],
            route: [],
            target,
          }
        }

        // Add menu to the suggestion
        suggestionMap[dateMenuNumber].menuIds.push(menuId)

        // Update route based on stop number
        suggestionMap[dateMenuNumber].route[stopNumber - 1] =
          suggestionMap[dateMenuNumber].menuIds.length - 1
      })

      // Convert map to array of DateMenuSuggestionImport
      const imports = Object.values(suggestionMap)

      if (imports.length === 0) {
        displayFailureMessage('No valid suggestions found in CSV')

        return
      }

      // Build the actual DateMenuSuggestion objects by matching menuIds to menus
      const importedSuggestions: DateMenuSuggestion[] = imports.map(
        (importData) => {
          const importMenus = importData.menuIds
            .map((id) => menus.find((menu) => menu.id === id))
            .filter((menu): menu is GOScheduleMenu => !!menu)

          // Make sure route is valid for the number of menus
          const importRoute =
            importData.route.length === importMenus.length
              ? importData.route
              : [...Array(importMenus.length).keys()] // Default sequential route if invalid

          const totalDistance = calculatePathDistance(
            importMenus.map((menu) => getMenuCoordinates(menu, hqId)),
            importRoute,
          )

          return {
            id: importData.id,
            menus: importMenus,
            target: importData.target,
            totalDistance,
            route: importRoute,
          }
        },
      )

      if (importedSuggestions.length === 0) {
        displayFailureMessage(
          'Could not match any menu IDs from the CSV to available menus',
        )

        return
      }

      return importedSuggestions
    } catch (error) {
      displayFailureMessage(
        `Failed to import CSV data. Please ensure the format matches the export: ${error}`,
      )
    }
  }
